import { GridItemProps } from '@chakra-ui/react';
import {
  ILegalEntityResponseSchema,
  ITaxConnectionSchema,
  IVertexTaxConnectionSchema,
  LegalEntityApplicationScope,
  TaxProviderTypeEnum,
  TaxStatusEnum,
} from '@monetize/types/app';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  useDeleteEntity,
  usePerformEntityAction,
} from '../../../../../../api/queryUtils';
import { settingsQueryKeys } from '../../../../../../api/settingsService';
import { TAX_IDENTIFIER_DISPLAY } from '../../../../../../constants/taxes';
import { TaxCardEdit } from '../TaxCardEdit';
import { TaxCardAction, TaxCardView } from '../TaxCardView';
import { VertexForm } from './VertexForm';

interface VertexTaxConnectionProps {
  connection: IVertexTaxConnectionSchema;
  legalEntities: Record<string, ILegalEntityResponseSchema>;
  verifyConnection: (provider: TaxProviderTypeEnum) => Promise<void>;
}

export const VertexTaxConnection = ({
  connection,
  legalEntities,
  verifyConnection,
}: VertexTaxConnectionProps) => {
  const queryClient = useQueryClient();

  const [mode, setMode] = useState<'VIEW' | 'EDIT'>('VIEW');
  const { mutateAsync: doUpdateStatus } =
    usePerformEntityAction<ITaxConnectionSchema>('tax', {
      meta: { showErrorToast: true },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: settingsQueryKeys.tax() });
      },
    });
  const { mutateAsync: doDeleteConnection } = useDeleteEntity('tax', {
    meta: { showErrorToast: true },
  });
  const isActive = connection.status === TaxStatusEnum.ACTIVE;

  const handleAction = async (action: TaxCardAction) => {
    switch (action) {
      case 'STATUS_UPDATE':
        await doUpdateStatus({
          id: TaxProviderTypeEnum.VERTEX,
          action: isActive ? TaxStatusEnum.INACTIVE : TaxStatusEnum.ACTIVE,
        });
        break;
      case 'VERIFY':
        await verifyConnection(TaxProviderTypeEnum.VERTEX);
        break;
      case 'DELETE':
        await doDeleteConnection({ id: TaxProviderTypeEnum.VERTEX });
        break;
      case 'OPEN':
        window.open('https://www.vertexinc.com/', '_blank');
        break;
      default:
        break;
    }
  };

  const getCredentialTypeName = (type?: string | null) => {
    if (type === 'OSeriesCloudCredentials') {
      return 'O Series Cloud Credentials';
    }
    if (type === 'OnDemandCredentials') {
      return 'On Demand Credentials';
    }
    return type || '';
  };

  const getIdentityProviderName = (provider?: string | null) => {
    return provider || '';
  };

  const tags: Array<{ label: string; value: string } & GridItemProps> = [
    {
      label: 'Credential Type',
      value: getCredentialTypeName(connection.credentials?.type),
    },
    {
      label: 'Tax Calculation Field',
      value: connection.configurations.identifier
        ? TAX_IDENTIFIER_DISPLAY[connection.configurations.identifier]
        : '',
    },
    {
      label: 'Taxpayer Code',
      value: connection.credentials?.companyCode || '',
    },
  ];

  // Add Identity Provider for OSeriesCloudCredentials
  if (connection.credentials?.type === 'OSeriesCloudCredentials') {
    tags.push({
      label: 'Identity Provider',
      value: getIdentityProviderName(connection.credentials?.identityProvider),
    });
  }

  // Add Base URL for OnDemandCredentials
  if (connection.credentials?.type === 'OnDemandCredentials') {
    tags.push({
      label: 'Base URL',
      value: connection.credentials?.baseUrl || '',
    });
  }

  tags.push(
    {
      label: 'Client ID',
      value: connection.credentials?.clientId
        ? connection.credentials.clientId.substring(0, 4) + '****'
        : '',
    },
    {
      label: 'Client Secret',
      value: connection.credentials?.clientSecret
        ? connection.credentials.clientSecret.substring(0, 4) + '****'
        : '',
      colSpan: 2,
    },
    {
      label: 'Apply to Which Legal Entities',
      value:
        connection.configurations.legalEntityOverride ===
        LegalEntityApplicationScope.ALL
          ? 'All Legal Entities'
          : connection.configurations.legalEntities
              .map((id) => legalEntities[id]?.name ?? id)
              .join(', '),
      colSpan: 2,
    },
  );

  return (
    <>
      {mode === 'VIEW' && (
        <TaxCardView
          status={connection.status}
          actions={[
            {
              action: 'STATUS_UPDATE',
              label: isActive ? 'Disable Connection' : 'Enable Connection',
              isDanger: false,
            },
            { action: 'VERIFY', label: 'Verify Connection' },
            { action: 'DELETE', label: 'Delete', isDanger: true },
          ]}
          tags={tags}
          type={TaxProviderTypeEnum.VERTEX}
          onEdit={() => setMode('EDIT')}
          onAction={handleAction}
        />
      )}
      {mode === 'EDIT' && (
        <TaxCardEdit type={TaxProviderTypeEnum.VERTEX}>
          <VertexForm
            connection={connection}
            handleCancel={() => setMode('VIEW')}
          />
        </TaxCardEdit>
      )}
    </>
  );
};
